/**
 * Splits a list template into its page zone and its record zone.
 *
 * A list template describes ONE card, and the page repeats it per record. But
 * some of what a builder wants on a list page belongs to the page rather than
 * to any record — a course catalogue, a notice, a sponsor strip. Repeating that
 * inside every card shows one identical list N times.
 *
 * So anything wrapped in `data-arcee-page` is lifted out and rendered ONCE,
 * above the cards. Everything else repeats.
 *
 *     <div data-arcee-page>
 *       <h2>Courses</h2>
 *       <ul data-arcee-repeat="courses"><li>{{ course.title }}</li></ul>
 *     </div>
 *
 *     <div class="card">…{{ event.title }}… repeats per record …</div>
 *
 * WHY A MARKER RATHER THAN A SECOND TEMPLATE. The builder edits one page and
 * sees the whole list in one place. A second Page Builder page for the header
 * would mean two things to create, two to keep in step, and a silent failure
 * when only one of them is configured.
 *
 * Parsing is done with DOMParser rather than a regular expression: the marker
 * can be nested, can carry any attributes, and its content can contain the same
 * tag names. A regex over HTML gets all three wrong.
 */

const PAGE_ZONE_SELECTOR = '[data-arcee-page]';

/**
 * Returns `{ pageHtml, recordHtml }`.
 *
 * `pageHtml` is '' when the template has no page zone, which is the ordinary
 * case — a template that only describes a card is unchanged by this.
 */
export function splitTemplate(html) {
  const source = String(html || '');
  if (!source.trim()) return { pageHtml: '', recordHtml: '' };

  // No marker: skip parsing entirely and hand back exactly what came in, so a
  // template that does not use this feature cannot be altered by it.
  if (!/data-arcee-page/i.test(source)) {
    return { pageHtml: '', recordHtml: source };
  }

  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div id="__root">${source}</div>`, 'text/html');
  } catch (e) {
    return { pageHtml: '', recordHtml: source };
  }

  const root = doc.getElementById('__root');
  if (!root) return { pageHtml: '', recordHtml: source };

  const zones = [...root.querySelectorAll(PAGE_ZONE_SELECTOR)];
  if (zones.length === 0) return { pageHtml: '', recordHtml: source };

  // Detached in document order, so several page zones keep the order the
  // builder wrote them in.
  const pageHtml = zones.map(zone => {
    const markup = zone.outerHTML;
    zone.remove();
    return markup;
  }).join('\n');

  return { pageHtml, recordHtml: root.innerHTML };
}

/**
 * The scope a page zone is rendered against: the related blocks, and no record.
 *
 * A page zone has no record by definition — it is drawn once for a list of
 * them — so `{{ event.title }}` inside one resolves to nothing rather than to
 * the first record's title, which would be a plausible-looking lie.
 */
export function buildPageScope(related) {
  const scope = {};
  for (const [alias, block] of Object.entries(related || {})) {
    scope[alias] = block && typeof block === 'object' && 'data' in block ? block.data : block;
  }
  return scope;
}
